import { useCallback, useEffect, useState } from "react";
import { Api, ApiError, type BlockedSite, type BlockedSites } from "../api.js";
import { useI18n } from "../i18n.js";

type What = { origin: string } | { suggestion: true };

/**
 * One website that tried to load an agent without being allowed, with Allow and Dismiss. Nothing is added until a
 * person presses Allow; the server only accepts sites that really tried (or the company website from their own e-mail).
 */
export function SiteRow({ api, agentId, label, sub, what, canAllow, allowText, allowLabel, before, onDone }: { api: Api; agentId: string; label: string; sub: string; what: What; canAllow: boolean; allowText: string; allowLabel: string; before?: () => Promise<void>; onDone: (note: string) => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (kind: "allow" | "dismiss") => {
    setBusy(true);
    setError(null);
    try {
      await before?.();
      if (kind === "allow") {
        const r = await api.allowSite(agentId, what);
        onDone(t("blocked.allowed", { entries: r.added.join(", ") }));
      } else {
        await api.dismissSite(agentId, what);
        onDone(t("blocked.dismissed"));
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("blocked.failed"));
      setBusy(false);
    }
  };
  return (
    <li className="blocked-site">
      <p>{label}</p>
      <p className="hint">{sub}</p>
      {canAllow ? (
        <div className="row">
          <button className="btn primary" disabled={busy} aria-label={allowLabel} onClick={() => void run("allow")}>{allowText}</button>
          <button className="btn secondary" disabled={busy} aria-label={t("blocked.dismissLabel", { origin: "origin" in what ? what.origin : label })} onClick={() => void run("dismiss")}>{t("blocked.dismiss")}</button>
        </div>
      ) : (
        <p className="hint">{t("blocked.ask")}</p>
      )}
      {error && <p className="field-error" role="alert">{error}</p>}
    </li>
  );
}

/** The Launch step's list. `saveNow` makes sure nothing is waiting to be saved before the server changes the draft; `onChanged` reloads the draft. */
export function BlockedSitesPanel({ api, agentId, saveNow, onChanged }: { api: Api; agentId: string; saveNow: () => Promise<void>; onChanged: () => Promise<void> }) {
  const { t } = useI18n();
  const [data, setData] = useState<BlockedSites | null>(null);
  const [notice, setNotice] = useState("");
  const load = useCallback(() => api.blockedSites(agentId).then(setData, () => setData(null)), [api, agentId]);
  useEffect(() => {
    void load();
  }, [load]);
  const done = async (note: string) => {
    setNotice(note);
    await onChanged();
    await load();
  };
  // Make the draft current on the server before it is changed there, so the next autosave does not clash with it.
  const before = () => saveNow().catch(() => {});
  const wrap = (fn: (note: string) => Promise<void>) => (note: string) => void fn(note);
  const rows = data?.sites ?? [];
  return (
    <>
      <p className="help">{t("blocked.help")}</p>
      <p aria-live="polite" className="hint">{notice}</p>
      {data && !rows.length && !data.suggestion && <p className="hint">{t("blocked.none")}</p>}
      <ul className="blocked-sites">
        {rows.map((s: BlockedSite) => (
          <SiteRow key={s.origin} before={before} api={api} agentId={agentId} label={t("blocked.tried", { origin: s.origin })} sub={t("blocked.adds", { entries: s.entries.join(", ") })} what={{ origin: s.origin }} canAllow={data?.canAllow ?? false} allowText={t("blocked.allow")} allowLabel={t("blocked.allowLabel", { origin: s.origin })} onDone={wrap(done)} />
        ))}
        {data?.suggestion && (
          <SiteRow before={before} api={api} agentId={agentId} label={t("blocked.suggest", { domain: data.suggestion.domain })} sub={t("blocked.adds", { entries: data.suggestion.entries.join(", ") })} what={{ suggestion: true }} canAllow={data.canAllow} allowText={t("blocked.suggestAllow", { domain: data.suggestion.domain })} allowLabel={t("blocked.suggestAllow", { domain: data.suggestion.domain })} onDone={wrap(done)} />
        )}
      </ul>
    </>
  );
}
